import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Button } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { readProductConfigOptions } from '../product-config-option';
import { useServicePriceTotal } from '../use-service-price-total';
import { TemplatePanel } from '../template-panel';
import { VCloudClassicTable } from './vcloud-classic-table';
import type { ProductTemplateProps } from '../template-props';
import type { ContractFormValues } from '../../contract-form';
import type { ContractServiceConfigOption } from '@/api/contracts';

const PACKAGE_MODE_OPTIONS = [
  { value: 'predefined', label: 'Predefined packages' },
  { value: 'custom', label: 'Custom quota (manual CPU/RAM)' },
];

/** Default item seeded for a new section row; commitment_prepaid MUST be boolean. */
function newSectionItem(title: string): ContractServiceConfigOption & { title: string } {
  return {
    name: '', title, type: title, unit: 'package',
    commitment_value: 1, commitment_price_per_unit: 0, commitment_time: 1,
    commitment_prepaid: true, statementable: false,
  };
}

/**
 * Cloud.VCloud VM instance builder. Writes into `products.{index}.service_configoptions`
 * as a flat array tagged by `title` ('vm-instance' | 'vm-lb' | 'vm-other').
 * Seeds one default vm-instance row on first mount; skips if already populated.
 */
export function VCloudTemplate({ index, detail }: ProductTemplateProps) {
  const { getValues, setValue, control } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;
  const options = (useWatch({ control, name: optsPath }) ?? []) as (ContractServiceConfigOption & { title?: string })[];

  const [packageMode, setPackageMode] = useState<'predefined' | 'custom'>('predefined');
  const configOptions = readProductConfigOptions(detail);

  // Derive the line's register price from Σ(qty × unit price) so the contract
  // Total (Σ register across products) reflects the configured VMs.
  useServicePriceTotal(index);

  // Seed once when empty (new product line; skip if resuming an existing contract).
  useEffect(() => {
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length > 0) return;
    setValue(optsPath, [newSectionItem('vm-instance')], { shouldDirty: true });
  }, [optsPath, getValues, setValue]);

  // Switching mode resets to one default vm-instance row to clear stale fields.
  const handleModeChange = (mode: 'predefined' | 'custom') => {
    setPackageMode(mode);
    const seed: ContractServiceConfigOption & { title: string } = {
      ...newSectionItem('vm-instance'),
      unit: mode === 'custom' ? 'custom' : 'package',
      ...(mode === 'custom' ? { vcpu: 0, ram: 0 } : {}),
    };
    setValue(optsPath, [seed], { shouldDirty: true });
  };

  if (!Array.isArray(options)) return null;

  const hasLb = options.some((o) => o.title === 'vm-lb');
  const hasOther = options.some((o) => o.title === 'vm-other');

  const appendSection = (title: string) => {
    const current = (getValues(optsPath) ?? []) as ContractServiceConfigOption[];
    setValue(optsPath, [...current, newSectionItem(title)] as never, { shouldDirty: true });
  };

  return (
    // TemplatePanel groups the whole VM builder so it reads as a distinct block,
    // separated from the base product fields (group / price / dates) above.
    <TemplatePanel title="Cloud configuration">
      <Field label="Configuration type">
        {(p) => (
          <Select value={packageMode} onValueChange={(v) => handleModeChange(v as 'predefined' | 'custom')}>
            <SelectTrigger {...p}><SelectValue /></SelectTrigger>
            <SelectContent>
              {PACKAGE_MODE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      <VCloudClassicTable optsPath={optsPath} type="vm-instance" packageMode={packageMode} configOptions={configOptions} />

      {hasLb && <VCloudClassicTable optsPath={optsPath} type="vm-lb" packageMode="predefined" configOptions={configOptions} />}

      {hasOther && <VCloudClassicTable optsPath={optsPath} type="vm-other" packageMode="predefined" configOptions={configOptions} />}

      {(!hasLb || !hasOther) && (
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
          {!hasLb && (
            <Button type="button" variant="secondary" size="sm" onClick={() => appendSection('vm-lb')}>
              + Add load balancer
            </Button>
          )}
          {!hasOther && (
            <Button type="button" variant="secondary" size="sm" onClick={() => appendSection('vm-other')}>
              + Add other service
            </Button>
          )}
        </div>
      )}
    </TemplatePanel>
  );
}
